import { ApolloError } from '@apollo/client'
import { GraphQLError } from 'graphql'

import Localization from '../localization/LocalizationManager'

const PARTIAL_BULK_FAILURE_CODE = 'PARTIAL_BULK_FAILURE'
const PHONE_CLAIMED_CODE = 'USER_PHONE_CLAIMED'
const MAX_DISPLAYED_ROWS = 10

interface IBulkRowFailure {
  rowIndex?: number
  errorCode?: string
  message?: string
  phoneNumber?: string | null
  holderUserId?: string | null
  holderName?: string | null
}

export interface IPhoneClaimConflict {
  rowIndex: number
  phoneNumber: string
  /** Current holder's id/name — present only when they're a member of this project. */
  holderUserId?: string | null
  holderName?: string | null
}

/**
 * Pulls the first "phone already claimed by another user" row out of a bulk-save
 * error (USER_PHONE_CLAIMED), so the caller can offer an admin the option to
 * transfer the number to the user being saved. Returns null when the error is not
 * a phone-claim conflict.
 */
export function extractPhoneClaimConflict(
  error: unknown,
): IPhoneClaimConflict | null {
  const graphQLError = ((error as ApolloError)?.graphQLErrors?.[0] ||
    error) as GraphQLError
  const extensions = graphQLError?.extensions
  if (extensions?.code !== PARTIAL_BULK_FAILURE_CODE) {
    return null
  }
  const details = extensions?.details as IBulkRowFailure[]
  if (!Array.isArray(details)) {
    return null
  }
  const row = details.find(
    d => d?.errorCode === PHONE_CLAIMED_CODE && !!d?.phoneNumber,
  )
  if (!row) {
    return null
  }
  return {
    rowIndex: typeof row.rowIndex === 'number' ? row.rowIndex : 0,
    phoneNumber: row.phoneNumber!,
    holderUserId: row.holderUserId,
    holderName: row.holderName,
  }
}

/**
 * Number of rows in a bulk-save error that failed because their phone is already
 * claimed by another user (USER_PHONE_CLAIMED) — used to offer a bulk reassign
 * after a CSV upload.
 */
export function countPhoneClaimConflicts(error: unknown): number {
  const graphQLError = ((error as ApolloError)?.graphQLErrors?.[0] ||
    error) as GraphQLError
  const extensions = graphQLError?.extensions
  if (extensions?.code !== PARTIAL_BULK_FAILURE_CODE) {
    return 0
  }
  const details = extensions?.details as IBulkRowFailure[]
  if (!Array.isArray(details)) {
    return 0
  }
  return details.filter(d => d?.errorCode === PHONE_CLAIMED_CODE).length
}

function translateErrorCode(errorCode: string): string {
  const { bulkSaveErrors } = Localization.translator

  switch (errorCode) {
    case 'INVALID_EMAIL':
      return bulkSaveErrors.invalidEmail
    case 'USER_REGISTRATION_INVALID_PHONE':
      return bulkSaveErrors.invalidPhone
    case 'USER_REGISTRATION_DUPLICATE_ROW':
      return bulkSaveErrors.duplicateRow
    case 'USER_REGISTRATION_MISSING_NAME':
      return bulkSaveErrors.missingName
    case 'USER_IDENTIFIER_CONFLICT':
      return bulkSaveErrors.identifierConflict
    case 'USER_REGISTRATION_AUTH_DENIED':
      return bulkSaveErrors.authDenied
    case 'USER_REGISTRATION_AUTH_UNAVAILABLE':
      return bulkSaveErrors.authUnavailable
    case 'USER_REGISTRATION_ROW_PROCESSING_FAILED':
      return bulkSaveErrors.rowProcessingFailed
    default:
      return null
  }
}

/**
 * Formats errors from partially tolerant bulk save mutations
 * (saveMembers / uploadUsersFromCsv).
 *
 * When the error carries the PARTIAL_BULK_FAILURE extension code, the
 * per-row failure details are rendered one per line (rowIndex is 0-based on
 * the wire and displayed 1-based, or as `rowLabels[rowIndex]` when given).
 * Known errorCodes are translated via the language tree; unknown ones fall
 * back to the wire message. Any other error falls back to its plain message.
 */
export function formatBulkSaveError(
  error: unknown,
  separator: string = '\n',
  rowLabels?: string[],
): string {
  if (!error) {
    return ''
  }

  const graphQLError = ((error as ApolloError)?.graphQLErrors?.[0] ||
    error) as GraphQLError
  const topLevelMessage = graphQLError?.message || String(error)

  const extensions = graphQLError?.extensions
  const details = extensions?.details as IBulkRowFailure[]

  if (
    extensions?.code !== PARTIAL_BULK_FAILURE_CODE ||
    !Array.isArray(details) ||
    !details.length
  ) {
    return topLevelMessage
  }

  const { bulkSaveErrors } = Localization.translator
  const lines = details.slice(0, MAX_DISPLAYED_ROWS).map(detail => {
    const rowNumber =
      typeof detail?.rowIndex === 'number' ? detail.rowIndex + 1 : '?'
    const message =
      translateErrorCode(detail?.errorCode) ||
      detail?.message ||
      detail?.errorCode ||
      bulkSaveErrors.unknownError
    const rowLabel =
      typeof detail?.rowIndex === 'number' ? rowLabels?.[detail.rowIndex] : null
    return rowLabel
      ? bulkSaveErrors.namedRowError(rowLabel, message)
      : bulkSaveErrors.rowError(rowNumber, message)
  })

  const hiddenRowsCount = details.length - MAX_DISPLAYED_ROWS
  if (hiddenRowsCount > 0) {
    lines.push(bulkSaveErrors.andXMoreRows(hiddenRowsCount))
  }

  // Localized header instead of the raw English wire message
  return [bulkSaveErrors.xRowsFailedToSave(details.length), ...lines].join(
    separator,
  )
}
